import { useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeIcon } from '@phosphor-icons/react/dist/csr/Eye';
import { UploadSimpleIcon } from '@phosphor-icons/react/dist/csr/UploadSimple';

import { Button } from '../../design-system/primitives/Button/Button';
import { Card } from '../../design-system/composites/Card/Card';
import { ClientDataTable } from '../../design-system/composites/DataTable/ClientDataTable';
import {
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import {
  readersImportApi,
  sensorsImportApi,
  type DeviceImportChange,
  type DeviceImportResult,
  type DeviceImportRow,
} from '../../lib/api/devices';
import { SegmentedControl } from '../../design-system/primitives/SegmentedControl/SegmentedControl';
import { FileField } from '../../design-system/primitives/FileField/FileField';
import styles from './DeviceImportScreen.module.css';

/**
 * The device stock intake — sensors and readers, each behind
 * `sensorsImportApi`/`readersImportApi`'s own two-call dry-run/apply shape
 * (Task 9), the SAME contract `ReferenceImportScreen.tsx` implements for
 * centres. Follows that component's shape throughout; the differences below
 * are the ones the API contract itself forces, not stylistic drift.
 *
 * ## `file` travels as a REAL upload, never text
 *
 * Unlike `centresImportApi`, which carries its CSV as a JSON string
 * (`ImportCentresRequest`'s own docblock), `POST /devices/{kind}/import` is
 * validated by `ImportDevicesRequest`, whose own docblock is explicit that
 * `file` is a genuine `multipart/form-data` upload — DELIBERATELY, so
 * `Serial::normalise()` reaches genuinely untrimmed bytes (Laravel's global
 * `TrimStrings` middleware never reaches inside an uploaded file). This
 * screen therefore keeps the chosen `File` object itself in state and hands
 * it straight to `sensorsImportApi`/`readersImportApi`, which build the
 * `FormData` (`devices.ts`'s own docblock) — never `.text()`, never a second
 * `Content-Type`.
 *
 * ## Two kinds, one screen, never two requests for the file not selected
 *
 * `kind` picks which import API this screen calls. Switching it clears the
 * chosen file, the preview and every failure — a sensor CSV and a reader CSV
 * have different columns, and carrying a sensor preview into the reader tab
 * would show verdicts for an API that was never actually called with what is
 * now on screen.
 *
 * ## Apply is unreachable without a preview OF THE CURRENT FILE
 *
 * Exactly `ReferenceImportScreen.tsx`'s own point 3: the button is rendered
 * unconditionally (never absent) so a mutant that quietly enables it has
 * something concrete to fail against, and stays disabled until `preview`
 * exists AND was taken against the exact `File` object now selected. A
 * `File` chosen a second time (even the identical bytes, re-picked from the
 * picker) is a DIFFERENT object reference — the same invalidation
 * `previewedFileText !== fileText` gives the string-bodied sibling, done here
 * by object identity because there is no string to compare.
 *
 * ## `changes` carries before/after VALUES, not only field names (review
 * round 3, Finding 1 — the reason this task exists)
 *
 * `DeviceImportRow.changes` (`devices.ts`) is
 * `readonly DeviceImportChange[] | null`, each entry a `{ field, before,
 * after }` triple `DeviceImport::classifyRow()` now publishes verbatim
 * (`app/Support/Device/DeviceImport.php`, API repo — `displayValue()`
 * formats both sides for display, dates as a fixed `Y-m-d`, never the DB's
 * raw storage string on one side and the CSV's typed format on the other).
 *
 * A field-NAME-only list (this screen's first version) catches half of
 * M-03's own defect — an unexpected column silently in play. It says
 * nothing about the other half: an EXPECTED column given a DESTRUCTIVE
 * value. `status: in_stock -> faulty` and `status: faulty -> in_stock` are
 * both just the word "status"; `notes: null -> "x"` and `notes: "x" ->
 * null` are both just "notes". M-03's own actual damage — reactivating a
 * deactivated centre — concentrates exactly there: `is_active` is a column
 * an operator already expects to see listed, so a name alone says nothing
 * about which direction it moved. `renderChanges` below is what closes
 * that gap; `test 1` in this component's own test file asserts the literal
 * `in_stock -> faulty` text, not merely that the word "status" appears.
 */

export interface DeviceImportScreenProps {
  permissions: readonly string[];
}

type Kind = 'sensor' | 'reader';

interface VerdictRow extends Row, DeviceImportRow {}

function toRow(row: DeviceImportRow): VerdictRow {
  return { ...row, id: String(row.row_number) };
}

const FORBIDDEN_CODE = 'forbidden';

export function DeviceImportScreen({ permissions }: DeviceImportScreenProps) {
  const { t } = useTranslation();
  const canManage = permissions.includes('device.manage');

  const [kind, setKind] = useState<Kind>('sensor');
  const [fileName, setFileName] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewedFile, setPreviewedFile] = useState<File | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState<DeviceImportResult | null>(null);
  const [previewFailure, setPreviewFailure] = useState<string | null>(null);
  const [previewReference, setPreviewReference] = useState<string | null>(null);
  const [applyFailure, setApplyFailure] = useState<string | null>(null);
  const [applyReference, setApplyReference] = useState<string | null>(null);
  const [applied, setApplied] = useState<DeviceImportResult | null>(null);

  const inFlight = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importApi = kind === 'sensor' ? sensorsImportApi : readersImportApi;

  // A KIND change (sensor <-> reader) starts over completely: a sensor CSV's
  // verdicts describe an API this screen is no longer even calling, so the
  // stale table would show rows for a request that will never happen again.
  function resetForKindChange(): void {
    setPreview(null);
    setPreviewedFile(null);
    setPreviewFailure(null);
    setPreviewReference(null);
    setApplyFailure(null);
    setApplyReference(null);
    setApplied(null);
  }

  // A NEW FILE, same kind, DELIBERATELY LEAVES `preview` ITSELF ALONE — only
  // `previewedFile` is cleared. Nulling `preview` here would make
  // `applyDisabled`'s own `file !== previewedFile` term dead code: `preview
  // === null` would already disable Apply on its own, and a mutant deleting
  // the identity check would have nothing left to fail against (measured:
  // exactly this mutation survived the suite before this comment existed).
  // The stale verdict table stays on screen — describing the FILE
  // `previewedFile` still names, not the one now chosen — until a fresh
  // preview replaces it or `applied` a moment later confirms the last one.
  function resetForNewFile(): void {
    setPreviewedFile(null);
    setPreviewFailure(null);
    setPreviewReference(null);
    setApplyFailure(null);
    setApplyReference(null);
    setApplied(null);
  }

  function describeFailure(error: ApiError, fallback: string): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('devices.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('devices.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default: {
        // Both refusals apply can answer — the 409 "data moved" and the 422
        // "token does not match the file" — carry a full, server-localised
        // sentence in `details.preview_token` (`errors.php`, API repo:
        // `device_import_data_moved` / `device_import_token_mismatch`, two
        // DIFFERENT sentences). Neither is replaced by a fixed client-side
        // sentence here: `error.status` distinguishes them for anyone
        // debugging, but the text itself already tells the two apart, and a
        // client-side rewrite could only make it vaguer.
        const [firstField] = Object.values(error.details);
        return firstField?.[0] ?? fallback;
      }
    }
  }

  function renderFailure(
    failureText: string | null,
    referenceText: string | null,
    referenceTestId: string,
  ): ReactNode {
    return (
      <div className={styles['failure']} role="alert" aria-atomic="true">
        {failureText === null ? null : <span>{failureText}</span>}
        {referenceText === null ? null : (
          <span className={styles['reference']}>
            {t('auth.supportReference')}{' '}
            <bdi dir="ltr" data-testid={referenceTestId}>
              {referenceText}
            </bdi>
          </span>
        )}
      </div>
    );
  }

  function handleKindChange(value: string): void {
    setKind(value as Kind);
    setFileName(null);
    setFile(null);
    if (fileInputRef.current !== null) fileInputRef.current.value = '';
    resetForKindChange();
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>): void {
    const chosen = event.target.files?.[0];
    if (chosen === undefined) return;

    setFileName(chosen.name);
    setFile(chosen);
    resetForNewFile();
  }

  async function handlePreview(): Promise<void> {
    if (inFlight.current || file === null) return;
    inFlight.current = true;
    setPreviewing(true);
    setPreviewFailure(null);
    setPreviewReference(null);
    setApplyFailure(null);
    setApplyReference(null);
    setApplied(null);

    try {
      const result = await importApi.preview(file);
      setPreview(result);
      setPreviewedFile(file);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setPreviewFailure(describeFailure(error, t('devices.import.previewFailed')));
      setPreviewReference(error.requestId ?? null);
    } finally {
      inFlight.current = false;
      setPreviewing(false);
    }
  }

  async function handleApply(): Promise<void> {
    if (inFlight.current || preview === null || file === null) return;
    inFlight.current = true;
    setApplying(true);
    setApplyFailure(null);
    setApplyReference(null);

    try {
      const result = await importApi.apply(file, preview.preview_token);
      setApplied(result);
      setPreview(result);
      // Same reasoning as `ReferenceImportScreen.tsx`: the write just
      // happened against exactly this file. Leaving `previewedFile` set
      // would leave Apply enabled beside "Import complete." — clearing it
      // forces a fresh preview before a second click can do anything.
      setPreviewedFile(null);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setApplyFailure(describeFailure(error, t('devices.import.applyUnknown')));
      setApplyReference(error.requestId ?? null);
    } finally {
      inFlight.current = false;
      setApplying(false);
    }
  }

  function renderVerdict(row: Row): ReactNode {
    const verdict = (row as unknown as DeviceImportRow).verdict;
    switch (verdict) {
      case 'create':
        return t('devices.import.verdictCreate');
      case 'update':
        return t('devices.import.verdictUpdate');
      case 'unchanged':
        return t('devices.import.verdictUnchanged');
      case 'reject':
        return t('devices.import.verdictReject');
      default:
        return verdict;
    }
  }

  // A `null` before/after is a real, displayable value (a cleared or a
  // never-set column), not "nothing to show" — `devices.import.valueEmpty`
  // names it explicitly rather than rendering an empty string a reader
  // could mistake for a missing cell.
  function formatChangeValue(value: DeviceImportChange['before']): string {
    return value === null ? t('devices.import.valueEmpty') : String(value);
  }

  // See this file's own docblock: `field`, `before` AND `after` — never
  // field names alone. `create`/`reject` render nothing (the em-dash "not
  // recorded" cell); an `update` whose `changes` is an EMPTY array is the
  // no-op update the dry run still classified as `update`, and gets its own
  // sentence rather than the blank cell a genuinely absent list gets.
  function renderChanges(row: Row): ReactNode {
    const importRow = row as unknown as DeviceImportRow;
    if (importRow.verdict !== 'update') return null;
    const changes = importRow.changes ?? [];
    if (changes.length === 0) return t('devices.import.changesEmpty');
    return (
      <ul className={styles['changesList']}>
        {changes.map((change) => (
          <li key={change.field}>
            {/* The field name, the arrow and both values are isolated
                together — never the row as a whole — the same reasoning
                `serial_number` gets its own `neverMirrors` column: a
                left-to-right "before -> after" reads correctly even under
                RTL layout only if nothing outside this `<bdi>` reorders it. */}
            <bdi dir="ltr">
              {change.field}: {formatChangeValue(change.before)} → {formatChangeValue(change.after)}
            </bdi>
          </li>
        ))}
      </ul>
    );
  }

  const columns: readonly Column[] = [
    { key: 'row_number', label: t('devices.import.columnRow'), numeric: true },
    { key: 'serial_number', label: t('devices.import.columnSerial'), neverMirrors: true },
    { key: 'verdict', label: t('devices.import.columnVerdict'), render: renderVerdict },
    { key: 'changes', label: t('devices.import.columnChanges'), render: renderChanges },
    { key: 'reason', label: t('devices.import.columnReason') },
  ];

  const verdictRows: readonly VerdictRow[] = preview === null ? [] : preview.rows.map(toRow);
  const status: ListStatus = verdictRows.length === 0 ? 'empty' : 'ready';
  const emptyBody = preview === null ? t('devices.import.noPreviewYet') : t('devices.import.noRows');

  // Disabled until a preview exists AND matches the file now selected — see
  // the component docblock. Rendered unconditionally so this attribute is
  // the one thing a mutant has to defeat.
  const applyDisabled =
    !canManage || preview === null || file !== previewedFile || applying || previewing;

  // Review round 3, Finding 2. `resetForNewFile` deliberately leaves
  // `preview` in place after a new file is chosen (see that function's own
  // comment) so `applyDisabled`'s identity check stays observable — but
  // that means the verdict table can sit on screen describing a file that
  // is no longer the one selected, unmarked, which is the same "the screen
  // asserts a fact about the wrong file" defect this task exists to
  // prevent, just moved from "after apply" to "after a second file choice".
  // `stale` names exactly that: a preview exists, but not of the current
  // file. Gated on `preview !== null` as well as the identity check so
  // nothing renders before a first preview has ever happened (the "no
  // preview yet" empty state already says that).
  const stale = preview !== null && file !== previewedFile;

  const kindOptions = [
    { value: 'sensor', label: t('devices.tabSensors') },
    { value: 'reader', label: t('devices.tabReaders') },
  ];

  return (
    <div className={styles['page']}>
      {/* One card (the design language's head: IconDisc + title, accent
          wash): intro, the controls (kind, file, Preview, Apply on one
          wrapping row) and the dry-run counts, all on the card ground. */}
      <Card wash icon={<UploadSimpleIcon size={16} />} title={t('devices.import.title')}>

        <p className={styles['intro']}>{t('devices.import.intro')}</p>

        {canManage ? (
          <div className={styles['fileRow']}>
            <SegmentedControl
              label={t('devices.import.kindLabel')}
              options={kindOptions}
              value={kind}
              onChange={handleKindChange}
            />

            <FileField
              ref={fileInputRef}
              id="deviceImportFile"
              label={t('devices.import.fileLabel')}
              accept=".csv,text/csv"
              fileName={fileName}
              emptyText={t('devices.import.noFileChosen')}
              onChange={handleFileChange}
            />

            <Button
              icon={<EyeIcon size={16} />}
              variant="secondary"
              loading={previewing}
              disabled={file === null || previewing}
              onClick={() => {
                void handlePreview();
              }}
            >
              {t('devices.import.preview')}
            </Button>

            <Button
              icon={<UploadSimpleIcon size={16} />}
              loading={applying}
              disabled={applyDisabled}
              onClick={() => {
                void handleApply();
              }}
            >
              {t('devices.import.apply')}
            </Button>
          </div>
        ) : null}

        {preview === null ? null : (
          <p className={styles['counts']}>
            {t('devices.import.countsSummary', {
              create: preview.counts.create,
              update: preview.counts.update,
              unchanged: preview.counts.unchanged,
              reject: preview.counts.reject,
            })}
          </p>
        )}
      </Card>

      {renderFailure(previewFailure, previewReference, 'deviceImportPreviewRequestId')}

      {renderFailure(applyFailure, applyReference, 'deviceImportApplyRequestId')}

      <p className={styles['success']} role="status">
        {applied === null
          ? ''
          : [
              t('devices.import.applied'),
              t('devices.import.countsSummary', {
                create: applied.counts.create,
                update: applied.counts.update,
                unchanged: applied.counts.unchanged,
                reject: applied.counts.reject,
              }),
            ].join(' ')}
      </p>

      {/* See `stale`'s own comment above. `role="status"`: this is new
          information about what the table below is now showing, not an
          error — the same politeness `ReferenceImportScreen.tsx`'s success
          banner uses. */}
      {stale ? (
        <p className={styles['stale']} role="status">
          {t('devices.import.stalePreview', { file: fileName ?? '' })}
        </p>
      ) : null}

      <div className={stale ? styles['staleTable'] : undefined}>
        <ClientDataTable
          caption={t('devices.import.title')}
          columns={columns}
          rows={verdictRows}
          status={status}
          empty={{ body: emptyBody }}
        />
      </div>
    </div>
  );
}
